import { isValidBirthDate } from '@/utils/booking';

export type DomainRestriction = {
  type: string;
  name: string;
  from?: number | string | null;
  to?: number | string | null;
  date_from?: string | null;
  date_to?: string | null;
};

export type SkierTypeConfig = {
  key: string;
  label: string;
  ageRange: string;
};

function formatAgeRange(from?: number | null, to?: number | null): string {
  if (from != null && to != null) return `${String(from)}–${String(to)} years old`;
  if (to != null) return `Under ${String(to)} years old`;
  if (from != null) return `${String(from)}+ years old`;
  return '';
}

function parseDateRestriction(dateStr: string | undefined | null): Date | null {
  if (!dateStr?.trim()) return null;
  const trimmed = dateStr.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const ddmmyyyyMatch = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(trimmed);
  if (ddmmyyyyMatch) {
    const [, d, m, y] = ddmmyyyyMatch;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function calculateAge(birthDate: Date, today: Date): number {
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return Math.max(0, age);
}

function formatDateRestrictionAsAge(
  dateFrom?: string | null,
  dateTo?: string | null,
  today: Date = new Date()
): string {
  const from = parseDateRestriction(dateFrom);
  const to = parseDateRestriction(dateTo);
  if (!from && !to) return '';
  const ageFrom = to ? calculateAge(to, today) : null;
  const ageTo = from ? calculateAge(from, today) : null;
  return formatAgeRange(ageFrom, ageTo);
}

export function formatRestrictionLabel(r: DomainRestriction): string {
  if (r.type === 'date') {
    const dateFrom = r.date_from ?? (typeof r.from === 'string' ? r.from : null);
    const dateTo = r.date_to ?? (typeof r.to === 'string' ? r.to : null);
    return formatDateRestrictionAsAge(dateFrom, dateTo);
  }
  return formatAgeRange(
    typeof r.from === 'number' ? r.from : undefined,
    typeof r.to === 'number' ? r.to : undefined
  );
}

export function buildSkierTypesFromRestrictions(
  restrictions: DomainRestriction[] | undefined
): SkierTypeConfig[] {
  const relevant = restrictions?.filter((r) => r.type === 'age' || r.type === 'date') ?? [];
  const seen = new Set<string>();

  return relevant
    .filter((r) => {
      const key = r.name.trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((r) => ({
      key: r.name.trim(),
      label: r.name,
      ageRange:
        r.type === 'date'
          ? formatDateRestrictionAsAge(
              r.date_from ?? (typeof r.from === 'string' ? r.from : null),
              r.date_to ?? (typeof r.to === 'string' ? r.to : null)
            )
          : formatAgeRange(
              typeof r.from === 'number' ? r.from : undefined,
              typeof r.to === 'number' ? r.to : undefined
            ),
    }))
    .sort((a, b) => {
      const aIsAdult = a.key.toLowerCase() === 'adult' ? 0 : 1;
      const bIsAdult = b.key.toLowerCase() === 'adult' ? 0 : 1;
      return aIsAdult - bIsAdult;
    });
}

export function getAllowedSkierKeysFromRestrictions(
  restrictions: DomainRestriction[] | undefined
): string[] | null {
  if (!restrictions?.length) return null;

  const relevant = restrictions.filter((r) => r.type === 'age' || r.type === 'date');
  if (relevant.length === 0) return null;

  const keys = new Set<string>();
  for (const r of relevant) {
    const key = r.name.trim();
    if (key) keys.add(key);
  }

  if (keys.size === 0) return null;
  return Array.from(keys);
}

export function getBirthDateFromDateRestriction(
  dateFrom?: string | null,
  dateTo?: string | null
): string | null {
  const from = parseDateRestriction(dateFrom);
  const to = parseDateRestriction(dateTo);
  const mid = from && to
    ? new Date((from.getTime() + to.getTime()) / 2)
    : from ?? to ?? null;
  if (!mid) return null;
  const y = String(mid.getFullYear());
  const m = String(mid.getMonth() + 1).padStart(2, '0');
  const d = String(mid.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getBirthDateFromAgeRestriction(
  r: DomainRestriction,
  referenceDate: string
): string | null {
  const from = typeof r.from === 'number' ? r.from : undefined;
  const to = typeof r.to === 'number' ? r.to : undefined;
  const midAge = from != null && to != null ? Math.round((from + to) / 2) : (from ?? to ?? 30);
  const ref = parseDateRestriction(referenceDate);
  if (!ref) return null;
  const birth = new Date(ref);
  birth.setFullYear(birth.getFullYear() - midAge);
  const y = String(birth.getFullYear());
  const m = String(birth.getMonth() + 1).padStart(2, '0');
  const d = String(birth.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getBirthDateFromRestriction(
  r: DomainRestriction,
  referenceDate?: string | null
): string | null {
  if (r.type === 'date') {
    const dateFrom = r.date_from ?? (typeof r.from === 'string' ? r.from : null);
    const dateTo = r.date_to ?? (typeof r.to === 'string' ? r.to : null);
    return getBirthDateFromDateRestriction(dateFrom, dateTo);
  }
  if (r.type === 'age' && referenceDate) {
    return getBirthDateFromAgeRestriction(r, referenceDate);
  }
  return null;
}

export function isBirthDateMatchingAnyCategory(
  birthDate: string,
  restrictions: DomainRestriction[] | undefined,
  referenceDate: string
): boolean {
  if (!isValidBirthDate(birthDate)) return false;

  const relevant = restrictions?.filter((r) => r.type === 'age' || r.type === 'date') ?? [];
  if (relevant.length === 0) return true;

  const birth = parseDateRestriction(birthDate);
  if (!birth) return false;

  const refDate = parseDateRestriction(referenceDate) ?? new Date();
  const age = calculateAge(birth, refDate);

  for (const r of relevant) {
    if (r.type === 'age') {
      const from = typeof r.from === 'number' ? r.from : undefined;
      const to = typeof r.to === 'number' ? r.to : undefined;
      if (from != null && age < from) continue;
      if (to != null && age > to) continue;
      return true;
    }
    if (r.type === 'date') {
      const dateFrom = parseDateRestriction(
        r.date_from ?? (typeof r.from === 'string' ? r.from : null)
      );
      const dateTo = parseDateRestriction(r.date_to ?? (typeof r.to === 'string' ? r.to : null));
      if (!dateFrom && !dateTo) return true;
      if (dateFrom && birth.getTime() < dateFrom.getTime()) continue;
      if (dateTo && birth.getTime() > dateTo.getTime()) continue;
      return true;
    }
  }
  return false;
}

function birthMatchesSingleRestriction(
  birth: Date,
  age: number,
  r: DomainRestriction
): boolean {
  if (r.type === 'age') {
    const from = typeof r.from === 'number' ? r.from : undefined;
    const to = typeof r.to === 'number' ? r.to : undefined;
    if (from != null && age < from) return false;
    if (to != null && age > to) return false;
    return true;
  }
  if (r.type === 'date') {
    const dateFrom = parseDateRestriction(
      r.date_from ?? (typeof r.from === 'string' ? r.from : null)
    );
    const dateTo = parseDateRestriction(r.date_to ?? (typeof r.to === 'string' ? r.to : null));
    if (!dateFrom && !dateTo) return true;
    if (dateFrom && birth.getTime() < dateFrom.getTime()) return false;
    if (dateTo && birth.getTime() > dateTo.getTime()) return false;
    return true;
  }
  return false;
}

/** True when the birth date falls in the age/date range for the restriction row matching `categoryName`. */
export function isBirthDateMatchingNamedCategory(
  birthDate: string,
  restrictions: DomainRestriction[] | undefined,
  categoryName: string,
  referenceDate: string
): boolean {
  if (!isValidBirthDate(birthDate)) return false;

  const needle = categoryName.trim().toLowerCase();
  const relevant =
    restrictions?.filter(
      (r) =>
        (r.type === 'age' || r.type === 'date') && r.name.trim().toLowerCase() === needle
    ) ?? [];
  if (relevant.length === 0) return true;

  const birth = parseDateRestriction(birthDate);
  if (!birth) return false;

  const refDate = parseDateRestriction(referenceDate) ?? new Date();
  const age = calculateAge(birth, refDate);

  for (const r of relevant) {
    if (birthMatchesSingleRestriction(birth, age, r)) return true;
  }
  return false;
}
